import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './SignUp.css';

const SignUp = ({ onBack }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && isRedirecting) {
      onBack();
    }
  }, [countdown, isRedirecting, onBack]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Nome de usuário é obrigatório';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Usuário deve ter pelo menos 3 caracteres';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Senha é obrigatória';
    } else if (formData.password.length < 3) {
      newErrors.password = 'Senha deve ter pelo menos 3 caracteres';
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Confirmação de senha é obrigatória';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'As senhas não coincidem';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage('');

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post('/api/auth/register', {
        username: formData.username,
        password: formData.password
      });

      if (response.data.success) {
        setSuccessMessage('✨ Conta criada com sucesso!');
        setFormData({ username: '', password: '', confirmPassword: '' });
        setIsRedirecting(true);
        setCountdown(5);
      } else {
        setErrors({ general: response.data.message || 'Erro ao criar conta' });
      }
    } catch (error) {
      if (error.response && error.response.data) {
        setErrors({ general: error.response.data.message || 'Erro ao criar conta' });
      } else {
        setErrors({ general: 'Erro de conexão. Tente novamente.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="signup-container">
      <div className="floating-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
        <div className="shape shape-5"></div>
      </div>
      
      <div className="signup-card">
        <div className="signup-header">
          <div className="logo-container">
            <div className="logo-circle">
              <span className="logo-text">SIA</span>
            </div>
          </div>
          <h1 className="main-title">Criar Nova Conta</h1>
          <p className="subtitle">Junte-se ao Portal Escolar SIA</p>
        </div>

        <form className="signup-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username" className="form-label">
              <span className="label-icon">👤</span>
              Nome de Usuário
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              className={`form-input ${errors.username ? 'error' : ''}`}
              placeholder="Escolha um nome de usuário"
              disabled={isLoading}
              autoComplete="username"
            />
            {errors.username && <span className="error-message">{errors.username}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              <span className="label-icon">🔒</span>
              Senha
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={`form-input ${errors.password ? 'error' : ''}`}
              placeholder="Crie uma senha segura"
              disabled={isLoading}
              autoComplete="new-password"
            />
            {errors.password && <span className="error-message">{errors.password}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              <span className="label-icon">🔐</span>
              Confirmar Senha
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
              placeholder="Confirme sua senha"
              disabled={isLoading}
              autoComplete="new-password"
            />
            {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
          </div>

          {errors.general && (
            <div className="error-general">
              <span className="error-icon">⚠️</span>
              {errors.general}
            </div>
          )}

          {successMessage && (
            <div className="success-general">
              <span className="success-icon">✨</span>
              {successMessage}
            </div>
          )}

          {isRedirecting && (
            <div className="redirect-message">
              <span className="redirect-icon">⏰</span>
              Redirecionando para a página inicial em ...{countdown}
            </div>
          )}

          <button type="submit" className="signup-button" disabled={isLoading || isRedirecting}>
            {isLoading ? (
              <>
                <span className="spinner"></span>
                Criando conta...
              </>
            ) : isRedirecting ? (
              <>
                <span className="button-icon">⏰</span>
                Redirecionando...
              </>
            ) : (
              <>
                <span className="button-icon">🚀</span>
                Criar Conta
              </>
            )}
          </button>
        </form>

        <div className="signup-footer">
          {!isRedirecting && (
            <button type="button" className="back-button" onClick={onBack}>
              <span className="back-icon">←</span>
              Voltar
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignUp;
